import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api,{
  ADMIN_API_URL,
  getAdminMe,
  getApprovalStats,
  listApprovals,
  approveSubmission,
  rejectSubmission
} from '../api';

import AdminShell from './AdminShell';

import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

const STATUS_META = {
  pending_review: {
    label: 'Under Review',
    cls: 'pending'
  },
  approved: {
    label: 'Approved',
    cls: 'active'
  },
  rejected: {
    label: 'Action Required',
    cls: 'expired'
  },
  not_submitted: {
    label: 'Not Submitted',
    cls: 'pending'
  }
};

const DOC_LABELS = {
  gst_certificate: 'GST',
  msme_certificate: 'MSME',
  identity_proof: 'Identity'
};

const TYPE_LABELS = {
  vendor_verification: 'Vendor Verification',
  gst: 'GST Application',
  msme: 'MSME Application',
  labour: 'Labour Application',
  certificate: 'Certificate Request',
  commission: 'Commission Request'
};

function formatDate(date) {
  if (!date) return '—';

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function timeAgo(date) {
  if (!date) return '';

  const timestamp = new Date(date).getTime();

  if (Number.isNaN(timestamp)) return '';

  const diffMs = Date.now() - timestamp;

  const minutes = Math.floor(diffMs / (1000 * 60));

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);

  return `${days}d ago`;
}

function initials(name) {
  if (!name) return '—';

  return name
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function getApplicationType(submission) {
  if (submission?.application_type) {
    return submission.application_type;
  }

  if (submission?.type) {
    return submission.type;
  }

  return 'vendor_verification';
}

function getTypeLabel(submission) {
  const type = getApplicationType(submission);

  return TYPE_LABELS[type] || type;
}

function getFee(submission) {
  if (submission?.fee !== undefined && submission?.fee !== null) {
    return submission.fee;
  }

  if (submission?.amount !== undefined && submission?.amount !== null) {
    return submission.amount;
  }

  return null;
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const number = Number(value);

  if (Number.isNaN(number)) return null;

  return `₹${number.toLocaleString('en-IN')}`;
}

export default function ApprovalsManagement() {
  const navigate = useNavigate();

  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);

  const [submissions, setSubmissions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [approvalType, setApprovalType] = useState('all');

  const [searchTerm, setSearchTerm] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState('');

  const [actionLoading, setActionLoading] = useState(false);

  const PAGE_SIZE = 5;

  /*
   * ------------------------------------------------------------
   * LOAD STATS
   * ------------------------------------------------------------
   */

  const loadStats = useCallback(async () => {
  try {
    const { data } = await getApprovalStats();
    console.log('APPROVAL STATS RESPONSE:', data);
    setStats(data);
  } catch (error) {
    console.error('Failed to load approval stats:', error);
  }
}, []);

  /*
   * ------------------------------------------------------------
   * LOAD APPROVALS
   * ------------------------------------------------------------
   */

  const loadSubmissions = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const params = {
          status:
            statusFilter === 'all'
              ? undefined
              : statusFilter,

          type: approvalType,

          search: searchTerm.trim() || undefined,

          region:
            regionFilter === 'all'
              ? undefined
              : regionFilter
        };

        const { data } = await listApprovals(params);

        setSubmissions(data?.submissions || []);
      } catch (error) {
        console.error('Failed to load approvals:', error);
        setSubmissions([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      statusFilter,
      approvalType,
      searchTerm,
      regionFilter
    ]
  );

  /*
   * ------------------------------------------------------------
   * INITIAL ADMIN LOAD
   * ------------------------------------------------------------
   */

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => {
        setAdmin(data.admin);
      })
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');

        navigate('/login');
      });

    loadStats();
  }, [navigate, loadStats]);

  /*
   * ------------------------------------------------------------
   * APPROVAL LIST
   * ------------------------------------------------------------
   */

  useEffect(() => {
    const timer = setTimeout(() => {
      loadSubmissions();
    }, 250);

    return () => clearTimeout(timer);
  }, [loadSubmissions]);

  /*
   * ------------------------------------------------------------
   * RESET PAGE WHEN FILTER CHANGES
   * ------------------------------------------------------------
   */

  useEffect(() => {
    setCurrentPage(1);
  }, [
    statusFilter,
    approvalType,
    searchTerm,
    regionFilter
  ]);

  /*
   * ------------------------------------------------------------
   * DERIVED DATA
   * ------------------------------------------------------------
   */

  const regions = useMemo(() => {
    const values = submissions
      .map((item) => item.vendor_city)
      .filter(Boolean);

    return [...new Set(values)].sort();
  }, [submissions]);

  const totalPages = Math.max(
    1,
    Math.ceil(submissions.length / PAGE_SIZE)
  );

  const visibleSubmissions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;

    return submissions.slice(
      start,
      start + PAGE_SIZE
    );
  }, [submissions, currentPage]);

  const firstVisible =
    submissions.length === 0
      ? 0
      : (currentPage - 1) * PAGE_SIZE + 1;

  const lastVisible = Math.min(
    currentPage * PAGE_SIZE,
    submissions.length
  );

  /*
   * ------------------------------------------------------------
   * APPROVE
   * ------------------------------------------------------------
   */

  const handleApprove = async (submission) => {
  setActionLoading(true);

  try {
    await approveSubmission(
      submission.id,
      undefined,
      getApplicationType(submission)
    );

    setSelectedSubmission(null);

    await Promise.all([
      loadSubmissions({ silent: true }),
      loadStats()
    ]);
  } catch (error) {
    alert(
      error.response?.data?.message ||
        'Failed to approve application.'
    );
  } finally {
    setActionLoading(false);
  }
};

  /*
   * ------------------------------------------------------------
   * OPEN REJECT
   * ------------------------------------------------------------
   */

  const openReject = (submission) => {
    setRejectTarget(submission);
    setRejectRemarks('');
  };

  /*
   * ------------------------------------------------------------
   * REJECT
   * ------------------------------------------------------------
   */

  const submitReject = async (event) => {
    event.preventDefault();

    if (!rejectRemarks.trim()) {
      return;
    }

    setActionLoading(true);

    try {
      await rejectSubmission(
  rejectTarget.id,
  rejectRemarks.trim(),
  getApplicationType(rejectTarget)
);

      setRejectTarget(null);
      setSelectedSubmission(null);

      await Promise.all([
        loadSubmissions({ silent: true }),
        loadStats()
      ]);
    } catch (error) {
      alert(
        error.response?.data?.message ||
          'Failed to reject application.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  /*
   * ------------------------------------------------------------
   * REFRESH
   * ------------------------------------------------------------
   */

  const handleRefresh = async () => {
    await Promise.all([
      loadSubmissions({ silent: true }),
      loadStats()
    ]);
  };

  /*
   * ------------------------------------------------------------
   * TAB DEFINITIONS
   * ------------------------------------------------------------
   */

  const tabs = [
    {
      key: 'all',
      label: 'All Pending',
      count: stats?.pendingReview ?? submissions.length
    },
    {
      key: 'vendor_verification',
      label: 'Vendor Verification',
      count: stats?.pendingVerification ?? '—'
    },
    {
      key: 'gst',
      label: 'GST Applications',
      count: stats?.pendingGst ?? '—'
    },
    {
      key: 'msme',
      label: 'MSME Applications',
      count: stats?.pendingMsme ?? '—'
    },
    {
      key: 'labour',
      label: 'Labour Applications',
      count: stats?.pendingLabour ?? '—'
    },
    {
      /*
       * UI placeholder for now.
       * Backend will be connected later.
       */
      key: 'commission',
      label: 'Commission Requests',
      count: stats?.commissionRequests ?? '—'
    }
  ];

  return (
    <AdminShell
      admin={admin}
      badges={{
        pendingApprovals:
          stats?.pendingReview
      }}
      pageTitle="Approvals Management"
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search vendors, GSTIN, PAN or application ID..."
    >
      <div className="glass-page">

        {/* =====================================================
            PAGE HEADER
        ====================================================== */}

        <div className="dash-welcome-row">
          <div>
            <h2 className="dash-welcome-title">
              Approvals Management

              {(stats?.pendingReview ?? 0) > 0 && (
                <span className="glass-pill-badge urgent">
                  {stats.pendingReview} Pending Action
                </span>
              )}
            </h2>

            <p className="dash-welcome-subtitle">
              Review and verify pending vendor registrations,
              certificate applications, and document uploads.
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center'
            }}
          >
            <button
              className="dash-modal-btn secondary"
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <span className="material-symbols-outlined">
                refresh
              </span>

              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>

            <button
              className="dash-modal-btn primary"
              type="button"
              onClick={() => {
                /*
                 * Export will be connected later.
                 */
                alert(
                  'Queue export will be connected to the backend.'
                );
              }}
            >
              <span className="material-symbols-outlined">
                download
              </span>

              Export Queue
            </button>
          </div>
        </div>

        {/* =====================================================
            KPI CARDS
        ====================================================== */}

        <section className="glass-kpi-grid">

          {/* Pending Vendor Onboarding */}

          <div className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Pending Vendor Onboarding</span>

              {(stats?.pendingReview ?? 0) > 0 && (
                <span className="glass-kpi-tag urgent">
                  Urgent
                </span>
              )}
            </div>

            <div className="glass-kpi-value">
              {stats?.pendingReview ?? '—'}
            </div>

            <div className="glass-kpi-sub">
              Awaiting Aadhaar &amp; bank/document verification
            </div>
          </div>

          {/* Certificate Applications */}

          <div className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Certificate Applications</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                verified
              </span>
            </div>

            <div className="glass-kpi-value">
              {stats?.certificateApplications ?? '—'}
            </div>

            <div className="glass-kpi-sub">
              GST / MSME / Labour applications
            </div>
          </div>

          {/* Average Turnaround */}

          <div className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Average Turnaround</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                schedule
              </span>
            </div>

            <div className="glass-kpi-value">
              {stats?.averageTurnaround
                ? `${stats.averageTurnaround} `
                : '—'}
            </div>

            <div className="glass-kpi-sub">
              SLA target &lt; 6.0 hrs
            </div>
          </div>

          {/* Rejection Rate */}

          <div className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Rejection Rate</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                rule
              </span>
            </div>

            <div className="glass-kpi-value">
              {stats?.rejectionRate !== undefined
                ? `${stats.rejectionRate}`
                : '—'}
            </div>

            <div className="glass-kpi-sub">
              Based on reviewed applications
            </div>
          </div>

          {/* Commission Requests */}

          <div className="glass-kpi-card">
            <div className="glass-kpi-top">
              <span>Commission Requests</span>

              <span className="material-symbols-outlined glass-kpi-icon">
                payments
              </span>
            </div>

            <div className="glass-kpi-value">
              {stats?.commissionRequests ?? '—'}
            </div>

            <div className="glass-kpi-sub">
              Sales executive payouts
            </div>

            <button
  type="button"
  onClick={() => navigate('/commissions')}
  style={{
    marginTop: 8,
    padding: 0,
    border: 'none',
    background: 'none',
    color: '#d64545',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left'
  }}
>
  View Commission Management →
</button>
          </div>

        </section>

        {/* =====================================================
            MAIN APPROVAL PANEL
        ====================================================== */}

        <div className="glass-panel glass-table-panel">

          {/* ---------------------------------------------------
              TABS
          ---------------------------------------------------- */}

          <div className="glass-tab-row">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`glass-tab ${
                  approvalType === tab.key
                    ? 'active'
                    : ''
                }`}
                onClick={() => {
                  setApprovalType(tab.key);

                  /*
                   * Commission backend is intentionally
                   * not connected yet.
                   */
                  if (tab.key === 'commission') {
                    setStatusFilter('pending_review');
                  }
                }}
              >
                {tab.label}

                {tab.count !== undefined &&
                  tab.count !== '—' && (
                    <span
                      style={{
                        marginLeft: 6,
                        opacity: 0.65
                      }}
                    >
                      ({tab.count})
                    </span>
                  )}
              </button>
            ))}
          </div>

          {/* ---------------------------------------------------
              COMMISSION PLACEHOLDER
          ---------------------------------------------------- */}

          {approvalType === 'commission' ? (
            <div
              className="dash-empty-state"
              style={{
                padding: '60px 20px'
              }}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: 42,
                  opacity: 0.45,
                  display: 'block',
                  marginBottom: 12
                }}
              >
                payments
              </span>

              <strong>
                Commission Requests
              </strong>

              <p style={{ marginTop: 8 }}>
                Commission approval workflow will be
                connected to the backend later.
              </p>
            </div>
          ) : (
            <>
              {/* ------------------------------------------------
                  FILTER BAR
              ------------------------------------------------- */}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 18,
                  flexWrap: 'wrap'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center'
                  }}
                >
                  <select
                    className="admin-text-input"
                    value={regionFilter}
                    onChange={(event) =>
                      setRegionFilter(event.target.value)
                    }
                    style={{
                      minWidth: 170
                    }}
                  >
                    <option value="all">
                      All Regions
                    </option>

                    {regions.map((region) => (
                      <option
                        key={region}
                        value={region}
                      >
                        {region}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    className="glass-icon-btn"
                    title="Refresh"
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    <span className="material-symbols-outlined">
                      refresh
                    </span>
                  </button>
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: 'rgba(40,45,52,0.55)'
                  }}
                >
                  {submissions.length > 0
                    ? `Showing ${firstVisible}–${lastVisible} of ${submissions.length}`
                    : 'No applications'}
                </div>
              </div>

              {/* ------------------------------------------------
                  TABLE
              ------------------------------------------------- */}

              <div className="dash-directory-table-wrap">

                {loading ? (
                  <div className="dash-empty-state">
                    Loading approvals...
                  </div>
                ) : submissions.length === 0 ? (
                  <div className="dash-empty-state">
                    No submissions match this filter.
                  </div>
                ) : (
                  <table className="dash-directory-table">

                    <thead>
                      <tr>
                        <th>Vendor / Applicant</th>
                        <th>Type &amp; Fee</th>
                        <th>Submitted</th>
                        <th>Assigned Executive</th>
                        <th>Verification Docs</th>
                        <th>Status</th>
                        <th className="right">
                          Actions
                        </th>
                      </tr>
                    </thead>

                    <tbody>

                      {visibleSubmissions.map((submission) => {
                        const meta =
                          STATUS_META[
                            submission.status
                          ] ||
                          STATUS_META.not_submitted;

                        const type =
                          getApplicationType(
                            submission
                          );

                        const fee =
                          formatCurrency(
                            getFee(submission)
                          );

                        return (
                          <tr
                            key={submission.id}
                          >

                            {/* Vendor */}

                            <td>
                              <div className="glass-vendor-cell">

                                <div className="glass-avatar-tile">
                                  {initials(
                                    submission.vendor_name
                                  )}
                                </div>

                                <div>
                                  <p className="dash-exec-name">
                                    {submission.vendor_name ||
                                      submission.applicant_name ||
                                      'Unknown Vendor'}
                                  </p>

                                  <p className="dash-exec-email">
                                    {submission.vendor_id
                                      ? `V-${String(
                                          submission.vendor_id
                                        ).padStart(
                                          5,
                                          '0'
                                        )}`
                                      : '—'}

                                    {submission.vendor_city
                                      ? ` • ${submission.vendor_city}`
                                      : ''}
                                  </p>
                                </div>

                              </div>
                            </td>

                            {/* Type + Fee */}

                            <td>
                              <div
                                style={{
                                  fontWeight: 650
                                }}
                              >
                                {getTypeLabel(
                                  submission
                                )}
                              </div>

                              {fee && (
                                <p className="dash-exec-email">
                                  {fee}
                                </p>
                              )}
                            </td>

                            {/* Submitted */}

                            <td>
                              <div>
                                {formatDate(
                                  submission.submitted_at
                                )}
                              </div>

                              <p className="dash-exec-email">
                                {timeAgo(
                                  submission.submitted_at
                                )}
                              </p>
                            </td>

                            {/* Executive */}

                            <td>
                              {submission.executive_name ? (
                                <div className="glass-vendor-cell">

                                  <span className="glass-avatar-tile small">
                                    {initials(
                                      submission.executive_name
                                    )}
                                  </span>

                                  <div>
                                    <div>
                                      {
                                        submission.executive_name
                                      }
                                    </div>

                                    {submission.executive_code && (
                                      <p className="dash-exec-email">
                                        {
                                          submission.executive_code
                                        }
                                      </p>
                                    )}
                                  </div>

                                </div>
                              ) : (
                                '—'
                              )}
                            </td>

                            {/* Documents */}

                            <td>
                              <div className="glass-doc-chips">

                                {!submission.documents ||
                                submission.documents.length ===
                                  0 ? (
                                  <span className="dash-exec-email">
                                    No docs uploaded
                                  </span>
                                ) : (
                                  submission.documents.map(
                                    (doc) => (
                                      <span
                                        key={doc.id}
                                        className={`glass-doc-chip ${
                                          doc.review_status ===
                                          'approved'
                                            ? 'approved'
                                            : doc.review_status ===
                                              'rejected'
                                            ? 'rejected'
                                            : ''
                                        }`}
                                      >

                                        <span className="material-symbols-outlined">
                                          {doc.review_status ===
                                          'approved'
                                            ? 'check'
                                            : doc.review_status ===
                                              'rejected'
                                            ? 'error'
                                            : 'description'}
                                        </span>

                                        {DOC_LABELS[
                                          doc.document_type
                                        ] ||
                                          doc.document_type}

                                      </span>
                                    )
                                  )
                                )}

                              </div>
                            </td>

                            {/* Status */}

                            <td>
                              <span
                                className={`dash-sub-status-pill ${meta.cls}`}
                              >
                                <span className="dash-status-dot" />

                                {meta.label}
                              </span>
                            </td>

                            {/* Actions */}

                            <td className="dash-row-actions">

                              <div
                                style={{
                                  display: 'flex',
                                  gap: 6,
                                  justifyContent:
                                    'flex-end',
                                  alignItems:
                                    'center'
                                }}
                              >

                                <button
                                  type="button"
                                  className="glass-icon-btn"
                                  title="Inspect Details"
                                  onClick={() =>
                                    setSelectedSubmission(
                                      submission
                                    )
                                  }
                                >
                                  <span className="material-symbols-outlined">
                                    visibility
                                  </span>
                                </button>

                                
                                  {['pending_review', 'submitted', 'under_review'].includes(submission.status) && (
                                  <>
                                    <button
                                      type="button"
                                      className="glass-icon-btn approve"
                                      title="Approve"
                                      onClick={() =>
                                        handleApprove(
                                          submission
                                        )
                                      }
                                      disabled={
                                        actionLoading
                                      }
                                    >
                                      <span className="material-symbols-outlined">
                                        check_circle
                                      </span>
                                    </button>

                                    <button
                                      type="button"
                                      className="glass-icon-btn reject"
                                      title="Reject"
                                      onClick={() =>
                                        openReject(
                                          submission
                                        )
                                      }
                                      disabled={
                                        actionLoading
                                      }
                                    >
                                      <span className="material-symbols-outlined">
                                        cancel
                                      </span>
                                    </button>
                                  </>
                                )}

                              </div>

                            </td>

                          </tr>
                        );
                      })}

                    </tbody>
                  </table>
                )}

              </div>

              {/* ------------------------------------------------
                  PAGINATION
              ------------------------------------------------- */}

              {submissions.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 18,
                    gap: 12
                  }}
                >

                  <span className="dash-exec-email">
                    {firstVisible}–{lastVisible} of{' '}
                    {submissions.length}
                  </span>

                  <div
                    style={{
                      display: 'flex',
                      gap: 6
                    }}
                  >

                    <button
                      type="button"
                      className="glass-icon-btn"
                      disabled={
                        currentPage <= 1
                      }
                      onClick={() =>
                        setCurrentPage(
                          (page) =>
                            Math.max(
                              1,
                              page - 1
                            )
                        )
                      }
                    >
                      <span className="material-symbols-outlined">
                        chevron_left
                      </span>
                    </button>

                    {Array.from(
                      {
                        length: totalPages
                      },
                      (_, index) =>
                        index + 1
                    ).map((page) => (
                      <button
                        key={page}
                        type="button"
                        className={`glass-tab ${
                          currentPage === page
                            ? 'active'
                            : ''
                        }`}
                        onClick={() =>
                          setCurrentPage(page)
                        }
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      type="button"
                      className="glass-icon-btn"
                      disabled={
                        currentPage >=
                        totalPages
                      }
                      onClick={() =>
                        setCurrentPage(
                          (page) =>
                            Math.min(
                              totalPages,
                              page + 1
                            )
                        )
                      }
                    >
                      <span className="material-symbols-outlined">
                        chevron_right
                      </span>
                    </button>

                  </div>

                </div>
              )}
            </>
          )}
        </div>

        {/* =====================================================
            SLA BANNER
        ====================================================== */}

        <div
          className="glass-panel"
          style={{
            marginTop: 0,
            marginBottom: 0,
            padding: '16px 20px'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}
          >
            <span className="material-symbols-outlined">
              priority_high
            </span>

            <div>
              <strong>
                Priority SLA Rule
              </strong>

              <span
                style={{
                  marginLeft: 8,
                  color:
                    'rgba(40,45,52,0.62)',
                  fontSize: 13
                }}
              >
                Vendor onboardings with payment
                deposits exceeding ₹10,000 must be
                reviewed within 4 hours.
              </span>
            </div>
          </div>

          <p
            style={{
              margin: '8px 0 0',
              fontSize: 12,
              color:
                'rgba(40,45,52,0.52)'
            }}
          >
            Automated GSTIN cross-checks are running
            live. Verification guidelines will be
            connected here later.
          </p>
        </div>

        {/* =====================================================
            DETAILS MODAL
        ====================================================== */}

        {selectedSubmission && (
          <div
            className="dash-modal-backdrop"
            onClick={() =>
              setSelectedSubmission(null)
            }
          >
            <div
              className="dash-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
            >

              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems: 'flex-start',
                  gap: 12
                }}
              >
                <div>
                  <h3 className="dash-modal-title">
                    Application Details
                  </h3>

                  <p className="dash-exec-email">
                    ID #{selectedSubmission.id}
                  </p>
                </div>

                <button
                  type="button"
                  className="glass-icon-btn"
                  onClick={() =>
                    setSelectedSubmission(
                      null
                    )
                  }
                >
                  <span className="material-symbols-outlined">
                    close
                  </span>
                </button>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(2, minmax(0, 1fr))',
                  gap: 16,
                  marginTop: 20
                }}
              >

                <div>
                  <span className="dash-exec-email">
                    Vendor
                  </span>

                  <div>
                    {selectedSubmission.vendor_name ||
                      '—'}
                  </div>
                </div>

                <div>
                  <span className="dash-exec-email">
                    Vendor ID
                  </span>

                  <div>
                    {selectedSubmission.vendor_id
                      ? `V-${String(
                          selectedSubmission.vendor_id
                        ).padStart(5, '0')}`
                      : '—'}
                  </div>
                </div>

                <div>
                  <span className="dash-exec-email">
                    Application Type
                  </span>

                  <div>
                    {getTypeLabel(
                      selectedSubmission
                    )}
                  </div>
                </div>

                <div>
                  <span className="dash-exec-email">
                    Submitted
                  </span>

                  <div>
                    {formatDate(
                      selectedSubmission.submitted_at
                    )}
                  </div>
                </div>

                <div>
                  <span className="dash-exec-email">
                    Assigned Executive
                  </span>

                  <div>
                    {selectedSubmission.executive_name ||
                      '—'}
                  </div>
                </div>

                <div>
                  <span className="dash-exec-email">
                    Status
                  </span>

                  <div>
                    {
                      (
                        STATUS_META[
                          selectedSubmission.status
                        ] ||
                        STATUS_META.not_submitted
                      ).label
                    }
                  </div>
                </div>

              </div>

              {/* Documents */}

<div
  style={{
    marginTop: 24
  }}
>
  <h4
    style={{
      margin: '0 0 12px'
    }}
  >
    Verification Documents
  </h4>

  {!selectedSubmission.documents ||
  selectedSubmission.documents.length === 0 ? (
    <p className="dash-exec-email">
      No documents uploaded.
    </p>
  ) : (
    <div className="glass-doc-chips">
      {selectedSubmission.documents.map((doc) => (
        <button
          type="button"
          key={doc.id}
          className={`glass-doc-chip ${
            doc.review_status === 'approved'
              ? 'approved'
              : doc.review_status === 'rejected'
              ? 'rejected'
              : ''
          }`}
          onClick={async () => {
  try {
    if (doc.mime_type === 'application/pdf' && doc.file_url?.startsWith('http')) {
      const response = await api.get(
        `/approvals/documents/${doc.id}/view`,
        {
          responseType: 'blob'
        }
      );

      const blobUrl = URL.createObjectURL(
        new Blob([response.data], {
          type: 'application/pdf'
        })
      );

      window.open(
        blobUrl,
        '_blank',
        'noopener,noreferrer'
      );

      // Give the new tab time to load before releasing the blob.
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 60000);

      return;
    }

    // Legacy/local files and non-PDF files
    const fileUrl = doc.file_url?.startsWith('http')
      ? doc.file_url
      : `${ADMIN_API_URL.replace('/api/admin', '')}${doc.file_url}`;

    window.open(
      fileUrl,
      '_blank',
      'noopener,noreferrer'
    );
  } catch (error) {
    console.error('Failed to open document:', error);

    alert(
      error.response?.data?.message ||
        'Failed to open document.'
    );
  }
}}
        >
          <span className="material-symbols-outlined">
            description
          </span>

          {DOC_LABELS[doc.document_type] ||
            doc.document_type}
        </button>
      ))}
    </div>
  )}
</div>

{/* Actions */}

{['pending_review', 'submitted', 'under_review'].includes(
  selectedSubmission.status
) && (
  <div className="dash-modal-actions">
    <button
      type="button"
      className="dash-modal-btn secondary"
      onClick={() =>
        openReject(selectedSubmission)
      }
      disabled={actionLoading}
    >
      Reject
    </button>

    <button
      type="button"
      className="dash-modal-btn primary"
      onClick={() =>
        handleApprove(selectedSubmission)
      }
      disabled={actionLoading}
    >
      {actionLoading
        ? 'Processing...'
        : 'Approve'}
    </button>
  </div>
)}

</div>
</div>
)}

        {/* =====================================================
            REJECT MODAL
        ====================================================== */}

        {rejectTarget && (
          <div
            className="dash-modal-backdrop"
            onClick={() =>
              setRejectTarget(null)
            }
          >
            <div
              className="dash-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
            >

              <h3 className="dash-modal-title">
                Reject Application
              </h3>

              <p className="dash-exec-email">
                {rejectTarget.vendor_name ||
                  'Application'}
              </p>

              <form
                onSubmit={submitReject}
                style={{
                  display: 'flex',
                  flexDirection:
                    'column',
                  gap: 16,
                  marginTop: 18
                }}
              >

                <div className="dash-modal-field">

                  <label htmlFor="rejectRemarks">
                    Reason for rejection
                  </label>

                  <textarea 
  id="rejectRemarks" 
  className="admin-text-input" 
  rows={4} 
  maxLength={500}  
  value={rejectRemarks}
  onChange={(e) => setRejectRemarks(e.target.value)}
  placeholder="e.g. GSTIN document is blurred / doesn't match PAN"
  required
/>

                </div>

                <div className="dash-modal-actions">

                  <button
                    type="button"
                    className="dash-modal-btn secondary"
                    onClick={() =>
                      setRejectTarget(null)
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="dash-modal-btn primary"
                    disabled={
                      actionLoading ||
                      !rejectRemarks.trim()
                    }
                  >
                    {actionLoading
                      ? 'Rejecting...'
                      : 'Confirm Rejection'}
                  </button>

                </div>

              </form>

            </div>
          </div>
        )}

      </div>
    </AdminShell>
  );
}